import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu'
import { Search, SlidersHorizontal, ArrowUpDown, X, LayoutGrid, List } from 'lucide-react'
import { cn } from '@/lib/utils'

export type SortField = 'name' | 'date' | 'size' | 'duration'
export type SortOrder = 'asc' | 'desc'
export type ViewMode = 'grid' | 'list'

export interface VideoFilters {
  search: string
  sortField: SortField
  sortOrder: SortOrder
  formats: string[]
  viewMode: ViewMode
}

interface VideoLibrarySortFilterProps {
  filters: VideoFilters
  onFiltersChange: (filters: VideoFilters) => void
  availableFormats?: string[]
  totalCount: number
  filteredCount: number
}

const sortFieldLabels: Record<SortField, string> = {
  name: 'Name',
  date: 'Date Added',
  size: 'File Size',
  duration: 'Duration',
}

export function VideoLibrarySortFilter({
  filters,
  onFiltersChange,
  availableFormats = [],
  totalCount,
  filteredCount,
}: VideoLibrarySortFilterProps) {
  const [searchValue, setSearchValue] = useState(filters.search)

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    // Debounce search
    const timeoutId = setTimeout(() => {
      onFiltersChange({ ...filters, search: value })
    }, 300)
    return () => clearTimeout(timeoutId)
  }

  const handleSortChange = (field: SortField) => {
    const newOrder: SortOrder =
      filters.sortField === field && filters.sortOrder === 'asc' ? 'desc' : 'asc'
    onFiltersChange({ ...filters, sortField: field, sortOrder: newOrder })
  }

  const handleFormatToggle = (format: string) => {
    const newFormats = filters.formats.includes(format)
      ? filters.formats.filter((f) => f !== format)
      : [...filters.formats, format]
    onFiltersChange({ ...filters, formats: newFormats })
  }

  const handleViewModeChange = (mode: ViewMode) => {
    onFiltersChange({ ...filters, viewMode: mode })
  }

  const clearFilters = () => {
    setSearchValue('')
    onFiltersChange({
      search: '',
      sortField: 'date',
      sortOrder: 'desc',
      formats: [],
      viewMode: filters.viewMode,
    })
  }

  const hasActiveFilters =
    filters.search || filters.formats.length > 0 || filters.sortField !== 'date'

  return (
    <div className="space-y-3">
      {/* Search and controls row */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search videos..."
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchValue && (
            <button
              onClick={() => {
                setSearchValue('')
                onFiltersChange({ ...filters, search: '' })
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Sort dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              <ArrowUpDown className="h-4 w-4" />
              <span className="hidden sm:inline">
                {sortFieldLabels[filters.sortField]}
              </span>
              <span
                className={cn(
                  'text-xs',
                  filters.sortOrder === 'asc' ? 'rotate-180' : ''
                )}
              >
                ↓
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Sort by</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={filters.sortField}
              onValueChange={(v) => handleSortChange(v as SortField)}
            >
              {Object.entries(sortFieldLabels).map(([field, label]) => (
                <DropdownMenuRadioItem key={field} value={field}>
                  {label}
                  {filters.sortField === field && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {filters.sortOrder === 'asc' ? '(A-Z)' : '(Z-A)'}
                    </span>
                  )}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Filter dropdown */}
        {availableFormats.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={cn('gap-2', filters.formats.length > 0 && 'border-primary')}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span className="hidden sm:inline">Filter</span>
                {filters.formats.length > 0 && (
                  <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-xs">
                    {filters.formats.length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Filter by format</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {availableFormats.map((format) => (
                <DropdownMenuCheckboxItem
                  key={format}
                  checked={filters.formats.includes(format)}
                  onCheckedChange={() => handleFormatToggle(format)}
                >
                  {format.toUpperCase()}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* View mode toggle */}
        <div className="flex border rounded-md">
          <Button
            variant={filters.viewMode === 'grid' ? 'default' : 'ghost'}
            size="icon"
            className="rounded-r-none"
            onClick={() => handleViewModeChange('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={filters.viewMode === 'list' ? 'default' : 'ghost'}
            size="icon"
            className="rounded-l-none"
            onClick={() => handleViewModeChange('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Active filters and results count */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span>
            {filteredCount === totalCount
              ? `${totalCount} video${totalCount !== 1 ? 's' : ''}`
              : `${filteredCount} of ${totalCount} videos`}
          </span>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          )}
        </div>

        {/* Active filter badges */}
        {filters.formats.length > 0 && (
          <div className="flex gap-1">
            {filters.formats.map((format) => (
              <span
                key={format}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs cursor-pointer hover:bg-primary/20"
                onClick={() => handleFormatToggle(format)}
              >
                {format.toUpperCase()}
                <X className="h-3 w-3" />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
